import type { QueueMessageType } from '@/api/types'

/**
 * QueueExtensions sends {type, executionAssemblyName, object} (camelCase), Availpro.Azure.Functions sends
 * {Type, Object}; in both the object is itself a JSON string. "none" is bare JSON or text.
 */
export type EnvelopeFormat = 'queue-extensions' | 'functions' | 'none'

/** What the compress side needs to rebuild a message; decoding fills one so a message can be edited and re-sent. */
export interface MessageDraft {
  format: EnvelopeFormat
  /** Assembly-qualified type name; receivers match on the class name, so "Namespace.Class" is enough. */
  type: string
  executionAssemblyName: string | null
  payload: string
}

export interface DecodedMessage {
  /** The payload: the envelope's inner object when there is one, pretty-printed when it is JSON. */
  text: string
  isJson: boolean
  compressed: boolean
  /** Short type name from the DI envelope, e.g. "RefreshExchangeRates". */
  messageType: string | null
  /** The whole envelope, pretty-printed, when the message had one. */
  envelope: string | null
  draft: MessageDraft
}

/** Azure Storage queues reject message text over 64 KiB. */
export const MAX_MESSAGE_BYTES = 64 * 1024

function base64ToBytes(value: string): Uint8Array {
  const clean = value.replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/')
  if (!clean || !/^[A-Za-z0-9+/]*={0,2}$/.test(clean)) throw new Error('Not a compressed message: the text is not base64.')
  const binary = atob(clean.padEnd(Math.ceil(clean.length / 4) * 4, '='))
  return Uint8Array.from(binary, (c) => c.charCodeAt(0))
}

// Chunked because spreading a large array into fromCharCode overflows the call stack.
function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(binary)
}

const isGzip = (bytes: Uint8Array) => bytes[0] === 0x1f && bytes[1] === 0x8b

async function pipe(bytes: Uint8Array | string, transform: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(transform)
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  try {
    return await pipe(bytes, new DecompressionStream('gzip'))
  } catch {
    throw new Error('The GZip data is truncated or corrupt; check the whole message was copied.')
  }
}

function tryParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

const pretty = (value: unknown) => JSON.stringify(value, null, 2)

/** Last segment of an assembly-qualified name, which is how the DI receivers match handlers. */
function shortTypeName(type: string): string {
  return type.split(',')[0]!.trim().split('.').pop()!
}

interface Envelope {
  format: EnvelopeFormat
  type: string
  executionAssemblyName: string | null
  payload: unknown
}

function unwrapEnvelope(value: unknown): Envelope | null {
  if (typeof value !== 'object' || value === null) return null
  const record = value as Record<string, unknown>
  const camel = typeof record.type === 'string'
  const type = camel ? record.type : record.Type
  const inner = camel ? record.object : record.Object
  if (typeof type !== 'string' || inner === undefined) return null
  return {
    format: camel ? 'queue-extensions' : 'functions',
    type,
    executionAssemblyName: typeof record.executionAssemblyName === 'string' ? record.executionAssemblyName : null,
    payload: typeof inner === 'string' ? (tryParse(inner) ?? inner) : inner,
  }
}

/**
 * Decodes a message as Storage Explorer or the portal shows it: base64(gzip(UTF-8 JSON)). Uncompressed
 * base64 JSON (the scraper's SDP and Python requester queues) is decoded too, and flagged as such.
 */
export async function decodeQueueMessage(input: string): Promise<DecodedMessage> {
  const bytes = base64ToBytes(input.trim())
  const compressed = isGzip(bytes)
  let text: string
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(compressed ? await gunzip(bytes) : bytes)
  } catch (error) {
    if (compressed) throw error
    throw new Error('Not a compressed message: the base64 decodes to neither GZip nor text.')
  }

  const bare = (text: string, isJson: boolean): DecodedMessage => ({
    text,
    isJson,
    compressed,
    messageType: null,
    envelope: null,
    draft: { format: 'none', type: '', executionAssemblyName: null, payload: text },
  })

  const parsed = tryParse(text)
  if (parsed === undefined) {
    if (!compressed) throw new Error('Not a compressed message: the base64 decodes to plain text, not GZip or JSON.')
    return bare(text, false)
  }

  const envelope = unwrapEnvelope(parsed)
  if (!envelope) return bare(pretty(parsed), true)
  const isJson = typeof envelope.payload !== 'string'
  const payloadText = isJson ? pretty(envelope.payload) : (envelope.payload as string)
  return {
    text: payloadText,
    isJson,
    compressed,
    messageType: shortTypeName(envelope.type),
    envelope: pretty(parsed),
    draft: { format: envelope.format, type: envelope.type, executionAssemblyName: envelope.executionAssemblyName, payload: payloadText },
  }
}

/** Builds a message the DI receivers accept: the envelope JSON, GZip, then base64, as the .NET senders do. */
export async function encodeQueueMessage(draft: MessageDraft): Promise<string> {
  const parsed = tryParse(draft.payload)
  if (draft.format !== 'none') {
    if (!draft.type.trim()) throw new Error('Enter the message type, e.g. "Namespace.Commands.RefreshExchangeRates, Assembly".')
    if (parsed === undefined) throw new Error('The payload must be valid JSON to go in an envelope.')
  }

  // Senders serialise compactly, so the payload is minified before it is wrapped.
  const payload = parsed === undefined ? draft.payload : JSON.stringify(parsed)
  const json =
    draft.format === 'queue-extensions'
      ? JSON.stringify({ type: draft.type.trim(), executionAssemblyName: draft.executionAssemblyName, object: payload })
      : draft.format === 'functions'
        ? JSON.stringify({ Type: draft.type.trim(), Object: payload })
        : payload
  return bytesToBase64(await pipe(json, new CompressionStream('gzip')))
}

/** Matches on "Namespace.Class", so a decoded type carrying a version or another assembly still finds its contract. */
export function findMessageType(types: readonly QueueMessageType[], type: string): QueueMessageType | undefined {
  const fullName = type.split(',')[0]!.trim()
  return fullName ? types.find((t) => t.type.split(',')[0] === fullName) : undefined
}

/**
 * The payload after picking `type`: a different known contract always brings its defaults, since a payload
 * for another message is no use. Re-picking the same contract keeps the payload, e.g. a decoded message.
 */
export function payloadForType(types: readonly QueueMessageType[], draft: MessageDraft, type: string): string {
  const next = findMessageType(types, type)
  if (!next) return draft.payload
  return next !== findMessageType(types, draft.type) || draft.payload.trim() === '' ? next.template : draft.payload
}
